import { Classes, Placement } from "@blueprintjs/core";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import {
  HexToolTipProps,
  HexTooltip,
} from "../../hex-components/HexTooltip.js";
import { useUniqueId } from "../../hooks/useUniqueId.js";
import { CustomIconProps } from "../icons/createCustomIcon.js";
import { StarActiveIcon } from "../icons/CustomIcons";

export interface StarIconCheckboxProps {
  id: HTMLInputElement["id"];
  onChange?: (starred: boolean) => void;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  starred?: HTMLInputElement["checked"];
  disabled?: HTMLInputElement["disabled"];
  className?: string;
  tooltipPlacement?: Placement;
  wrapInput?: boolean;
}

const StarIconCheckboxInternal: React.FunctionComponent<
  StarIconCheckboxProps
> = ({
  className,
  disabled = false,
  id,
  onChange,
  onClick,
  starred,
  tooltipPlacement,
}) => {
  const renderTarget = useCallback<
    Exclude<HexToolTipProps["renderTarget"], undefined>
  >(
    ({ isOpen: _isOpen, ...targetProps }) => {
      return (
        <Root className={className}>
          <Checkbox
            {...targetProps}
            disabled={disabled}
            onCheckedChange={onChange}
            onClick={onClick}
          />
          <Icon />
        </Root>
      );
    },
    [className, disabled, onChange, onClick],
  );

  return (
    <Provider id={id} starred={starred}>
      <Tooltip placement={tooltipPlacement} renderTarget={renderTarget} />
    </Provider>
  );
};

export const StarIconCheckbox = styled(StarIconCheckboxInternal)``;

const StyledSpan = styled.span`
  & {
    position: relative;
    display: inline-flex;

    color: ${({ theme }) => theme.iconColor};

    &[data-checked="true"] {
      color: ${({ theme }) => theme.userActionColor};
      .${Classes.ICON} {
        color: inherit;
      }
      &&&&&&&&&&& span svg {
        color: ${({ theme }) => theme.userActionColor};
      }
    }
  }
`;

const StyledInput = styled.input`
  &[type="checkbox"] {
    display: inline-flex;
    border-radius: ${({ theme }) => theme.borderRadius};

    inset: 0;
    position: absolute;

    &:focus-visible,
    &:focus-within,
    &:focus {
      outline-width: 1.5px;
      outline-offset: 0.5px;
    }

    cursor: pointer;
    border: 0;
    margin: 0;
    height: 100%;
    width: 100%;
    appearance: none;

    background: transparent;

    & ~ span {
      border-radius: ${({ theme }) => theme.borderRadius};
      padding: 2px;
      svg > path {
        transition: fill-opacity
          ${({ theme }) =>
            `${theme.animation.duration} ${theme.animation.easing}`};
        transition: all
          ${({ theme }) =>
            `${theme.animation.duration} ${theme.animation.easing}`};
        fill-opacity: 0;
      }
    }

    &:not(:checked) ~ span {
      svg path:first-of-type {
        fill-opacity: 0;
      }
    }

    &:hover:not(:disabled) ~ span {
      svg path:first-of-type {
        fill-opacity: 0.35;
      }
    }

    &:active:not(:disabled) ~ span {
      svg path:first-of-type {
        fill-opacity: 1;
      }
    }
  }
`;

type ContextValue = {
  starred: boolean;
  id: string;
};

const Context = React.createContext<ContextValue | undefined>(undefined);

const useContext = (): ContextValue => {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error("StarIconCheckbox must be used within a Provider");
  }
  return context;
};

interface ProviderProps
  extends Omit<React.ComponentProps<typeof Context.Provider>, "value"> {
  starred: boolean | undefined;
  /** A unique, stable identifier. If not provided, a random id will be generated. */
  id?: string;
}

const Provider = React.memo(function Provider(props: ProviderProps) {
  const { id: idProp, starred = false, ...rest } = props;

  // eslint-disable-next-line react-hooks/rules-of-hooks -- consumer is wrong to change id
  const id = idProp ?? useUniqueId();

  const value: ContextValue = useMemo(() => ({ starred, id }), [starred, id]);

  return <Context.Provider value={value} {...rest} />;
});

const Root = React.memo(
  React.forwardRef<HTMLSpanElement, React.ComponentProps<typeof StyledSpan>>(
    function Root(props, ref) {
      const { id, starred } = useContext();
      return <StyledSpan data-checked={starred} id={id} {...props} ref={ref} />;
    },
  ),
);

const DEFAULT_UNSTARRED_LABEL = "Add to favorites";
const DEFAULT_STARRED_LABEL = "Remove from favorites";
function getLabel(checked: boolean | undefined): string {
  return checked ? DEFAULT_STARRED_LABEL : DEFAULT_UNSTARRED_LABEL;
}

export interface StarControlTooltipProps extends Partial<HexToolTipProps> {}

const Tooltip = React.memo(function Tooltip(props: StarControlTooltipProps) {
  const { starred } = useContext();
  return <HexTooltip content={getLabel(starred)} {...props} />;
});

export interface StarControlCheckboxProps
  extends Partial<React.InputHTMLAttributes<HTMLInputElement>> {
  onCheckedChange?: (
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
}

const Checkbox = React.memo(
  React.forwardRef<HTMLInputElement, StarControlCheckboxProps>((props, ref) => {
    const {
      checked: checkedProp,
      disabled,
      onChange,
      onCheckedChange,
      onClick,
      ...rest
    } = props;

    const { id, starred } = useContext();

    const checked = checkedProp ?? starred;
    const label = getLabel(checked);

    const handleClick = useCallback<React.MouseEventHandler<HTMLInputElement>>(
      (event) => {
        event.stopPropagation();
        onClick?.(event);
      },
      [onClick],
    );

    const handleChange = useCallback<
      React.ChangeEventHandler<HTMLInputElement>
    >(
      (event) => {
        onChange?.(event);
        if (event.defaultPrevented) return;
        onCheckedChange?.(event.target.checked, event);
      },
      [onChange, onCheckedChange],
    );

    return (
      <StyledInput
        ref={ref}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        id={id}
        type="checkbox"
        onChange={handleChange}
        onClick={handleClick}
        {...rest}
      />
    );
  }),
);

export interface StarControlIconProps extends CustomIconProps {}

const Icon = React.memo(
  React.forwardRef<HTMLSpanElement, StarControlIconProps>((props, ref) => {
    const { id } = useContext();
    return (
      <StarActiveIcon
        ref={ref}
        aria-labelledby={id}
        color="currentColor"
        {...props}
      />
    );
  }),
);

/**
 * @example
 * <StarControl.Provider starred>
 *   <StarControl.Tooltip
 *     renderTarget={({ isOpen: _isOpen, ...tooltipTargetProps }) => (
 *       <StarControl.Root>
 *         <StarControl.Checkbox onCheckedChange {...tooltipTargetProps} />
 *         <StarControl.Icon />
 *       </StarControl.Root>
 *     )}
 *   />
 * </StarControl.Provider>
 */
export const StarControl = Object.freeze({
  Checkbox,
  Icon,
  Root,
  Provider,
  Tooltip,
});
